/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import DefaultApiLimit from '../../config/defaultLimit';
import { filterType } from '../../config/enum';
import { CommonFilterProperties, FilterUpdateProperties } from '../../interface/filter.interface';
import {
  FetchReportProperties,
  FilterReportInterFace,
  ReportInitialProperties,
  RequestReportInterFace,
} from '../../interface/reportPerformanceLocation.interface';
import {
  ClearFilters,
  ConfigureListFilterResponse,
  RemoveSingleFilterFromList,
  UpdateCommonFilter,
} from '../../shared-functions/CommonFilters';
import { removeEmptyKeys } from '../../shared-functions/DateTime';
import report from '../../utils/api/reports';

interface AccountParameters {
  accountCustomId?: string;
}

// a thunk to fetch stations
export const fetchPerformanceReportStation = createAsyncThunk<
  FetchReportProperties,
  RequestReportInterFace
>('reports/fetchPerformanceReportStation', async (data: RequestReportInterFace, { dispatch }) => {
  try {
    const response = await report.fetchPerformanceReportListStation(removeEmptyKeys(data));
    return response.data;
  } catch (error: any) {
    dispatch({
      type: 'notification/setNotificationDetails',
      payload: {
        type: 'error',
        notificationDetails: {
          statusCode: error?.response?.data?.code || 200,
          message: error?.response?.data?.message || 'Something went wrong!',
        },
      },
    });
    throw error;
  }
});

// a thunk to refresh stations
export const refreshPerformanceReportStation = createAsyncThunk<
  FetchReportProperties,
  RequestReportInterFace
>('reports/refreshPerformanceReportStation', async (data: RequestReportInterFace) => {
  const response = await report.fetchPerformanceReportListStation(removeEmptyKeys(data));
  return response.data;
});

export const fetchListFilters = createAsyncThunk<CommonFilterProperties[], AccountParameters>(
  'reports/fetchListPerformanceStationFilters',
  async (data: AccountParameters, { dispatch }) => {
    try {
      const response = await report.fetchListStationFilters(data);
      const updatedResponse: CommonFilterProperties[] = ConfigureListFilterResponse(response.data);
      return updatedResponse;
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationDetails',
        payload: {
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 200,
            message: error?.response?.data?.message || 'Something went wrong!',
          },
        },
      });
      throw error;
    }
  },
);

const stationFilter: CommonFilterProperties[] = [
  {
    name: 'Search here...',
    key: 'searchBy',
    type: filterType.TEXT,
    value: '',
    applyFilter: false,
    enable: true,
    searchBy: true,
  },
];

// Initial state
const initialState: ReportInitialProperties = {
  reports: [],
  loading: false,
  page: 1,
  limit: DefaultApiLimit.limit,
  totalPages: 1,
  totalResults: 0,
  filter: {},
  filters: stationFilter,
  filterListLoading: false,
  isFilterUpdated: false,
  sortBy: null,
  sortField: null,
  isPerformanceStationReportUpdate: false, // Add this line
};

// Create the slice
export const reportsPerformanceStationSlice = createSlice({
  name: 'reportsPerformanceStation',
  initialState,
  reducers: {
    /**
     * Set User filter data function
     * @param {any} state - The current state.
     * @param {any} action - The action object.
     */
    setReportFilterData: (
      state: ReportInitialProperties,
      action: PayloadAction<FilterReportInterFace>,
    ) => {
      state.filter = action.payload;
    },
    /**
     * Set User filters data function
     * @param {any} state - The current state.
     * @param {any} action - The action object.
     */
    setReportFiltersData: (
      state: ReportInitialProperties,
      action: PayloadAction<CommonFilterProperties[]>,
    ) => {
      state.filters = action.payload;
    },

    /**
     * Set User filter data function
     * @param {any} state - The current state.
     * @param {any} action - The action object.
     */
    setReportsData: (
      state,
      action: PayloadAction<{
        name: keyof ReportInitialProperties;
        value: string | number | undefined;
      }>,
    ) => {
      const { name, value } = action.payload;
      state[name] = value as never; // 👈 force TS to accept dynamic assignment
    },
    /**
     * Function that reset slice to initial state
     * @param {any} state - The current state.
     */
    resetReportSliceToInitialState: (state) => {
      Object.assign(state, initialState);
    },

    /**
     * Explicitly set the Performance station report update flag
     * @param {any} state - The current state.
     * @param {any} action - The action object.
     */
    setPerformanceStationReportUpdate: (state, action: PayloadAction<boolean>) => {
      state.isPerformanceStationReportUpdate = action.payload;
    },

    /**
     * Update station management filters
     * @param {any} state - The current state.
     * @param {any} action - The action object.
     */
    updateReportFilters: (state, action: PayloadAction<FilterUpdateProperties>) => {
      const payload = { ...action.payload };
      if (payload.type === 'add') {
        const { updatedCommonFilter, isFilterUpdated } = UpdateCommonFilter(
          state.filters,
          action.payload,
        );
        state.filters = updatedCommonFilter;
        state.isFilterUpdated = isFilterUpdated;
        // state.filters = UpdateCommonFilter(state.filters, action.payload);
      } else if (payload.type === 'remove') {
        state.filters = RemoveSingleFilterFromList(state.filters, payload.key);
        state.isFilterUpdated = true;
      } else if (payload.type === 'clear') {
        state.filters = ClearFilters(state.filters);
        state.isFilterUpdated = true;
        state.sortBy = null;
        state.sortField = null;
      }
    },
    /**
     * function for set sortBy
     * @param {any} state - The current state.
     * @param {any} action - The action object.
     */
    setSortData: (state, action: PayloadAction<{ sortField: string; sortBy: 'asc' | 'desc' }>) => {
      state.sortBy = action.payload.sortBy;
      state.sortField = action.payload.sortField;
    },
  },
  /**
   * Extra reducers to handle thunk request
   * @param {any} builder - The builder object.
   */
  extraReducers: (builder) => {
    builder
      .addCase(fetchPerformanceReportStation.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchPerformanceReportStation.fulfilled, (state, action) => {
        state.loading = false;
        state.page = action.payload.page;
        state.limit = action.payload.limit;
        state.totalPages = action.payload.totalPages;
        state.totalResults = action.payload.totalResults;
        state.reports = action.payload.results;
      })
      .addCase(fetchPerformanceReportStation.rejected, (state) => {
        state.loading = false;
      });
    builder.addCase(refreshPerformanceReportStation.fulfilled, (state, action) => {
      state.page = action.payload.page;
      state.limit = action.payload.limit;
      state.totalPages = action.payload.totalPages;
      state.totalResults = action.payload.totalResults;
      state.reports = action.payload.results;
    });
    builder
      .addCase(fetchListFilters.pending, (state) => {
        state.filterListLoading = true;
      })
      .addCase(
        fetchListFilters.fulfilled,
        (state, action: PayloadAction<CommonFilterProperties[]>) => {
          state.filterListLoading = false;
          state.filters = [...(state.filters || []), ...action.payload];
        },
      )
      .addCase(fetchListFilters.rejected, (state) => {
        state.filterListLoading = false;
      });
  },
});

// Export the actions
export const {
  setReportFilterData,
  setReportFiltersData,
  setReportsData,
  resetReportSliceToInitialState,
  updateReportFilters,
  setSortData,
  setPerformanceStationReportUpdate,
} = reportsPerformanceStationSlice.actions;

// Export the reducer
export default reportsPerformanceStationSlice.reducer;
